import { flowService } from 'v2/shared/services/flow-service'
import { EvalEvents, evalEventBus } from 'v2/utils/event-bus/eval-events'
import { agentLogger } from 'v2/utils/logger'
import { errorToast } from 'v2/utils/toasts'
import { useFlowStore } from '../../store'
import type { GenericEdge, GenericNode } from '../../types'
import { cleanNodesForExport } from '../../utils/node-cleanup'
import { useSimulationStore } from './simulation-store'
import { useEvalStore } from './store'

const SSE_EVENT_TO_EVAL_EVENT = {
  started: EvalEvents.STARTED,
  dataset_generation_started: EvalEvents.DATASET_GENERATION_STARTED,
  dataset_generation_started_node: EvalEvents.DATASET_GENERATION_STARTED_NODE,
  dataset_generation_completed: EvalEvents.DATASET_GENERATION_COMPLETED,
  dataset_generation_test_case: EvalEvents.DATASET_GENERATION_TEST_CASE,
  evaluation_tests_started_for_node: EvalEvents.TESTS_STARTED_FOR_NODE,
  evaluation_result: EvalEvents.RESULT,
  completed: EvalEvents.COMPLETED,
  eval_cancelled: EvalEvents.CANCELLED,
  node_evaluation_skipped: EvalEvents.NODE_EVALUATION_SKIPPED,
  node_queued: EvalEvents.NODE_QUEUED,
} as const

const isEvalSSEEvent = (event: string): event is keyof typeof SSE_EVENT_TO_EVAL_EVENT =>
  Object.hasOwn(SSE_EVENT_TO_EVAL_EVENT, event)

const serializeErrorPayload = (value: unknown) => {
  if (value instanceof Error) {
    return {
      name: value.name,
      message: value.message,
    }
  }
  return value
}

export const prepareEvalSidebarForRun = () => {
  const { setShowEvalSidebar } = useFlowStore.getState()
  setShowEvalSidebar(true)
}

const handleEvalStreamEvent = (eventName: string | undefined, data: unknown) => {
  if (!eventName) return

  // Extract eval_id and run_id from payload
  const evalId =
    data && typeof data === 'object' && 'eval_id' in data
      ? (data as { eval_id: string }).eval_id
      : null

  const runId =
    data && typeof data === 'object' && 'run_id' in data
      ? (data as { run_id: number }).run_id
      : null

  // Get current eval_id and run_id from store
  const currentEvalId = useEvalStore.getState().currentEvalId
  const currentRunId = useEvalStore.getState().currentRunId

  // Filter out events that don't match current eval_id
  // Allow events when currentEvalId is null (initial state) or when evalId is null (error events)
  if (evalId && currentEvalId && evalId !== currentEvalId) {
    agentLogger.debug('Discarding SSE event with mismatched eval_id', {
      eventName,
      receivedEvalId: evalId,
      currentEvalId,
    })
    return
  }

  // Filter out events that don't match current run_id (except for lifecycle events)
  // Allow events when currentRunId is null (initial state) or when runId is null
  const isLifecycleEvent = eventName === 'started' || eventName === 'error'

  if (!isLifecycleEvent && runId !== null && currentRunId !== null && runId !== currentRunId) {
    agentLogger.debug('Discarding SSE event with mismatched run_id', {
      eventName,
      receivedRunId: runId,
      currentRunId,
    })
    return
  }

  if (eventName === 'error') {
    handleEvalStreamError(data)
    return
  }

  if (isEvalSSEEvent(eventName)) {
    evalEventBus.emit(SSE_EVENT_TO_EVAL_EVENT[eventName], data)
  } else {
    agentLogger.debug('Unhandled eval stream event', { eventName, data })
  }
}

export const handleEvalStreamError = (error: unknown) => {
  const payload = serializeErrorPayload(error)
  evalEventBus.emit(EvalEvents.ERROR, payload)
  agentLogger.error('Eval stream error', error)
}

const handleEvalStreamCompleted = () => {
  evalEventBus.emit(EvalEvents.STREAM_ENDED)
  agentLogger.info('Eval stream ended')
}

export const startEvalStream = (
  nodes: GenericNode[],
  edges: GenericEdge[],
  evalId?: string | null,
) => {
  // Get system prompt and agent settings from store
  const { systemPrompt } = useFlowStore.getState()
  const { agentSettings } = useFlowStore.getState()

  let currentRunId = useEvalStore.getState().currentRunId
  if (currentRunId !== null) {
    // increment run id
    currentRunId = currentRunId + 1
    useEvalStore.getState().setCurrentRunId(currentRunId)
  }

  const ctrl = flowService.startEvalStream(
    {
      flow_json: {
        nodes: cleanNodesForExport(nodes),
        edges: edges,
        system_prompt: systemPrompt || null,
        agent_settings: agentSettings || null,
      },
      ...(evalId && { eval_id: evalId }),
    },
    {
      onMessage: () => {},
      onEvent: (eventName, data) => {
        handleEvalStreamEvent(eventName, data)
      },
      onError: (e) => {
        handleEvalStreamError(e)
      },
      onDone: () => {
        handleEvalStreamCompleted()
      },
    },
  )

  useEvalStore.getState().setSSEController(ctrl)
  useEvalStore.getState().setIsEvalRunning(true)
}

/**
 * SSE event name → handler mapping for simulation eval events.
 * Terminal events (simulation_completed, simulation_error) end the stream.
 */
const handleSimulationStreamEvent = (eventName: string | undefined, data: unknown) => {
  if (!eventName) return
  useSimulationStore.getState().addEvent(eventName, data)
}

const handleSimulationStreamError = (error: unknown) => {
  useSimulationStore.getState().addEvent('error', serializeErrorPayload(error))
  errorToast({
    title: 'Simulation eval error',
    description: error instanceof Error ? error.message : 'Stream error',
  })
}

const handleSimulationStreamCompleted = () => {
  useSimulationStore.getState().addEvent('stream_ended', { message: 'SSE stream closed' })
}

/**
 * Run pre-selected persisted scenarios via /simulation/scenarios/run endpoint.
 * Streams results into useSimulationStore.
 */
export const runSelectedScenariosStream = (
  phloUuid: string,
  scenarioUuids: string[],
  flowJson: Record<string, unknown>,
  maxTurns: number,
) => {
  const ctrl = flowService.runSelectedScenariosStream(
    {
      flow_json: flowJson,
      phlo_uuid: phloUuid,
      scenario_uuids: scenarioUuids,
      max_turns: maxTurns,
    },
    {
      onMessage: () => {},
      onEvent: (eventName, data) => {
        handleSimulationStreamEvent(eventName, data)
      },
      onError: (e) => {
        handleSimulationStreamError(e)
      },
      onDone: () => {
        handleSimulationStreamCompleted()
      },
    },
  )

  useSimulationStore.getState().setSseController(ctrl)
  return ctrl
}

/**
 * Generate scenarios via /simulation/scenarios/generate endpoint.
 * Streams progress / scenario_saved / completed / error events into
 * useSimulationStore so the scenarios panel can render generation state the
 * same way it does for runs, regardless of whether the trigger is the manual
 * UI button or a Vibe Agent tool call (whose events arrive via the eval
 * bridge and land in the same store).
 */
export const generateScenariosStream = (
  phloUuid: string,
  flowJson: Record<string, unknown>,
  maxScenarios: number,
  instructions?: string,
) => {
  useSimulationStore.getState().clear()
  const trimmedInstructions = instructions?.trim()
  const ctrl = flowService.generateSimulationScenarios(
    {
      flow_json: flowJson,
      phlo_uuid: phloUuid,
      max_scenarios: maxScenarios,
      ...(trimmedInstructions && {
        test_case_generation_instructions: trimmedInstructions,
      }),
    },
    {
      onMessage: () => {},
      onEvent: (eventName, data) => {
        handleSimulationStreamEvent(eventName, data)
      },
      onError: (e) => {
        handleSimulationStreamError(e)
      },
      onDone: () => {
        handleSimulationStreamCompleted()
      },
    },
  )

  useSimulationStore.getState().setSseController(ctrl)
  return ctrl
}
